import Money from "@dashboard/components/Money";
import { OrderErrorFragment, OrderLineFragment } from "@dashboard/graphql";
import { commonMessages } from "@dashboard/intl";
import { useOptionalOrderLineDiscountContext } from "@dashboard/products/components/OrderDiscountProviders/OrderLineDiscountProvider";
import { productPath } from "@dashboard/products/urls";
import { Box, Button, Skeleton, Text } from "@saleor/macaw-ui-next";
import { ExternalLink, Pencil, Trash2 } from "lucide-react";
import { useState } from "react";
import { useIntl } from "react-intl";
import { Link } from "react-router-dom";

import { columnsMessages, messages as detailsMessages } from "../OrderDetailsDatagrid/messages";
import { getOrderErrors, getOrderLineStatus } from "../OrderDraftDetailsDatagrid/datagrid";
import { messages as draftMessages } from "../OrderDraftDetailsDatagrid/messages";
import { messages as lineDiscountMessages } from "../OrderLineDiscountDialog/messages";
import { messages } from "./messages";

interface OrderLinesMobileListEditing {
  errors: OrderErrorFragment[];
  onQuantityChange: (id: string, quantity: number) => void;
  onRemove: (id: string) => void;
}

interface OrderLinesMobileListProps {
  lines: OrderLineFragment[];
  loading?: boolean;
  onShowMetadata: (id: string) => void;
  // Lines become editable (quantity stepper + remove) when set.
  editing?: OrderLinesMobileListEditing;
  // Offer "Edit price / discount" (needs OrderLineDiscountProvider).
  canEditLineDiscount?: boolean;
}

const THUMBNAIL_SIZE = 48;

const LineThumbnail = ({ url }: { url?: string | null }) => {
  const [failed, setFailed] = useState(false);

  return (
    <Box
      __width={THUMBNAIL_SIZE}
      __height={THUMBNAIL_SIZE}
      __minWidth={THUMBNAIL_SIZE}
      borderRadius={3}
      overflow="hidden"
      backgroundColor="default2"
      borderStyle="solid"
      borderWidth={1}
      borderColor="default1"
    >
      {url && !failed && (
        <img
          src={url}
          alt=""
          loading="lazy"
          onError={() => setFailed(true)}
          style={{ width: "100%", height: "100%", objectFit: "contain" }}
        />
      )}
    </Box>
  );
};

const QuantityStepper = ({
  lineId,
  quantity,
  onChange,
}: {
  lineId: string;
  quantity: number;
  onChange: (id: string, quantity: number) => void;
}) => {
  const intl = useIntl();
  // Remounted (via `key`) when the saved quantity changes, which resets this.
  const [value, setValue] = useState(String(quantity));

  const commit = (next: number) => {
    if (!Number.isFinite(next) || next < 0) {
      setValue(String(quantity));

      return;
    }

    setValue(String(next));

    if (next !== quantity) {
      onChange(lineId, next);
    }
  };

  return (
    <Box display="flex" alignItems="center" gap={1}>
      <Button
        type="button"
        variant="secondary"
        size="small"
        aria-label={intl.formatMessage(messages.decreaseQuantity)}
        disabled={quantity <= 1}
        onClick={() => commit(quantity - 1)}
        data-test-id="mobile-line-quantity-decrease"
      >
        −
      </Button>
      <input
        type="number"
        inputMode="numeric"
        min={0}
        value={value}
        aria-label={intl.formatMessage(columnsMessages.quantity)}
        onChange={event => setValue(event.target.value)}
        onBlur={() => commit(parseInt(value, 10))}
        onKeyDown={event => {
          if (event.key === "Enter") {
            (event.target as HTMLInputElement).blur();
          }
        }}
        data-test-id="mobile-line-quantity-input"
        style={{
          width: 48,
          height: 32,
          textAlign: "center",
          fontSize: 16,
          borderRadius: 6,
          border: "1px solid var(--mu-colors-border-default1)",
          background: "var(--mu-colors-background-default1)",
          color: "inherit",
        }}
      />
      <Button
        type="button"
        variant="secondary"
        size="small"
        aria-label={intl.formatMessage(messages.increaseQuantity)}
        onClick={() => commit(quantity + 1)}
        data-test-id="mobile-line-quantity-increase"
      >
        +
      </Button>
    </Box>
  );
};

/**
 * Phone-friendly replacement for the order line datagrids: one stacked card per
 * line with everything visible at once and touch-sized actions, instead of a
 * canvas grid that needs horizontal scrolling and long-press menus.
 */
export const OrderLinesMobileList = ({
  lines,
  loading,
  onShowMetadata,
  editing,
  canEditLineDiscount = false,
}: OrderLinesMobileListProps) => {
  const intl = useIntl();
  const getLineDiscount = useOptionalOrderLineDiscountContext();
  const lineDiscountEnabled = (canEditLineDiscount || !!editing) && !!getLineDiscount;

  if (loading) {
    return (
      <Box paddingX={4} paddingY={3} display="flex" flexDirection="column" gap={3}>
        <Skeleton />
        <Skeleton />
      </Box>
    );
  }

  if (!lines.length) {
    return (
      <Box paddingX={4} paddingY={4}>
        <Text size={3} color="default2">
          {intl.formatMessage(draftMessages.emptyText)}
        </Text>
      </Box>
    );
  }

  return (
    <Box as="ul" margin={0} padding={0} data-test-id="order-lines-mobile-list">
      {lines.map((line, index) => {
        const productId = line.variant?.product?.id;
        const statuses = editing
          ? getOrderLineStatus(intl, line, getOrderErrors(editing.errors, line.id))
          : [];
        const isDiscounted =
          !!line.undiscountedUnitPrice?.gross &&
          line.undiscountedUnitPrice.gross.amount > line.unitPrice.gross.amount;
        const details = [line.variant?.name, line.productSku].filter(Boolean).join(" · ");

        return (
          <Box
            as="li"
            key={line.id}
            listStyleType="none"
            paddingX={4}
            paddingY={3}
            borderTopStyle={index === 0 ? undefined : "solid"}
            borderTopWidth={index === 0 ? 0 : 1}
            borderColor="default1"
            data-test-id="order-line-mobile"
          >
            <Box display="flex" gap={3} alignItems="flex-start">
              <LineThumbnail url={line.thumbnail?.url} />
              <Box display="flex" flexDirection="column" gap={1} __minWidth={0} __flex="1">
                <Text size={3} fontWeight="medium" style={{ overflowWrap: "anywhere" }}>
                  {line.productName}
                </Text>
                {details && (
                  <Text size={2} color="default2" style={{ overflowWrap: "anywhere" }}>
                    {details}
                  </Text>
                )}
                {line.isGift && (
                  <Text size={2} color="success1">
                    {intl.formatMessage(columnsMessages.isGift)}
                  </Text>
                )}
                {statuses.map(status => (
                  <Text
                    key={status.status}
                    size={2}
                    color={status.type === "error" ? "critical1" : "warning1"}
                  >
                    {status.status}
                  </Text>
                ))}
              </Box>
            </Box>

            <Box
              display="flex"
              alignItems="center"
              justifyContent="space-between"
              flexWrap="wrap"
              gap={2}
              marginTop={3}
            >
              {editing ? (
                <QuantityStepper
                  key={line.quantity}
                  lineId={line.id}
                  quantity={line.quantity}
                  onChange={editing.onQuantityChange}
                />
              ) : (
                <Text size={3} color="default2">
                  {line.quantity} ×{" "}
                  {isDiscounted && (
                    <Box as="span" marginRight={1} style={{ textDecoration: "line-through" }}>
                      <Money money={line.undiscountedUnitPrice.gross} />
                    </Box>
                  )}
                  <Money money={line.unitPrice.gross} />
                </Text>
              )}
              <Text size={4} fontWeight="bold">
                <Money money={line.totalPrice.gross} />
              </Text>
            </Box>
            {editing && (
              <Text size={2} color="default2" display="block" marginTop={1}>
                {intl.formatMessage(columnsMessages.price)}:{" "}
                {isDiscounted && (
                  <Box as="span" marginRight={1} style={{ textDecoration: "line-through" }}>
                    <Money money={line.undiscountedUnitPrice.gross} />
                  </Box>
                )}
                <Money money={line.unitPrice.gross} />
              </Text>
            )}

            <Box display="flex" flexWrap="wrap" gap={2} marginTop={3}>
              {productId && (
                <Link to={productPath(productId)} target="_blank">
                  <Button type="button" variant="tertiary" size="small" as="span">
                    <ExternalLink size={14} />
                    {intl.formatMessage(detailsMessages.productDetails)}
                  </Button>
                </Link>
              )}
              {lineDiscountEnabled && getLineDiscount && (
                <Button
                  type="button"
                  variant="tertiary"
                  size="small"
                  onClick={() => getLineDiscount(line.id).openDialog()}
                  data-test-id="mobile-line-edit-price"
                >
                  <Pencil size={14} />
                  {intl.formatMessage(lineDiscountMessages.menuItem)}
                </Button>
              )}
              <Button
                type="button"
                variant="tertiary"
                size="small"
                onClick={() => onShowMetadata(line.id)}
              >
                {intl.formatMessage(commonMessages.viewMetadata)}
              </Button>
              {editing && (
                <Button
                  type="button"
                  variant="tertiary"
                  size="small"
                  color="critical1"
                  onClick={() => editing.onRemove(line.id)}
                  data-test-id="mobile-line-remove"
                >
                  <Trash2 size={14} />
                  {intl.formatMessage(draftMessages.deleteOrder)}
                </Button>
              )}
            </Box>
          </Box>
        );
      })}
    </Box>
  );
};
